import React from "react";

/* =============================================================================
 * 语雀导出的 Markdown 字符串 → React 节点树
 *
 * 口径来自 yuque-format-test（README.md / HANDLING.md / reference.html）：
 *   - 参照实现是该项目里的 build-reference.js（它输出 HTML 字符串）
 *   - 这里改成直接产出 React 元素，不拼 HTML、不用 dangerouslySetInnerHTML
 *   - 版式（字号、颜色、间距）全部在 ./index.less，和 reference.html 一一对应
 *
 * 支持的语法：标题 1～6（5/6 级按四级版式）、段落（行尾两空格硬换行）、
 * 无序 / 有序 / 无限嵌套 / 任务列表、引用（可嵌套）、代码块（行号 + 语法高亮）、
 * 表格（列对齐 + 单元格 <br/>）、分割线、独占一段的图片、链接（新窗口 + rel）、
 * ::: 高亮块、语雀的细节块（重建 details）、行内 <font color> / <font background-color>
 * / <u> / <sup> / <sub> / <br>。
 *
 * 安全口径：不执行任何原始 HTML。白名单之外的标签（<div> 等）一律当纯文本渲染；
 * 链接、图片的 javascript: / data: / vbscript: 协议会被丢弃。
 * ========================================================================== */

const HTML_COMMENT_RE = /<!--[\s\S]*?-->/g;
const LIST_ITEM_RE = /^(\s*)([-+*]|\d+\.)\s+(.*)$/;
const STANDALONE_IMAGE_RE = /^!\[[^\]]*\]\([^)]+\)$/;
/* 行内占位符：\u0000 + 类型 + 序号 + \u0001。a=原子元素，o=开标签，c=闭标签 */
const MARK_RE = /\u0000([aoc])(\d+)\u0001/g;
/* 段落硬换行哨兵：必须和上面的行内占位符分开（占位符以 \u0000 开头）。
   否则链接文字递归走 parseInline 时，「图片包在链接里」已经生成的图片占位符
   会被当成硬换行，变成 <a><br></a> 且漏出 "aN" 文字。\u0002 也在顶部清理的
   C0 控制字符区间里，正文自己伪造不出来。 */
const HARD_BREAK_MARKER = "\u0002";

/** 与 build-reference.js 保持一致的代码高亮关键字表 */
const KEYWORDS = new Set([
  "function",
  "const",
  "let",
  "var",
  "return",
  "if",
  "else",
  "for",
  "while",
  "class",
  "new",
  "import",
  "export",
  "from",
  "async",
  "await",
  "try",
  "catch",
  "throw",
  "typeof",
  "instanceof",
  "this",
  "true",
  "false",
  "null",
  "undefined",
  "def",
  "elif",
  "lambda",
  "print",
  "int",
  "float",
  "str",
  "bool",
  "void",
  "public",
  "private",
  "static",
]);

function createCtx() {
  return { atoms: [], tags: [], keySeq: 0 };
}

function nextKey(ctx) {
  return "yq-" + ctx.keySeq++;
}

/** 建元素时统一挂 key：解析结果会以数组形式作为 children 渲染 */
function h(ctx, type, props, ...children) {
  return React.createElement(
    type,
    { ...(props || {}), key: nextKey(ctx) },
    ...children
  );
}

function atom(ctx, node) {
  const index = ctx.atoms.push(node) - 1;
  return "\u0000a" + index + "\u0001";
}

function openMark(ctx, type, tag, props) {
  const index = ctx.tags.push({ type, tag, props }) - 1;
  return "\u0000o" + index + "\u0001";
}

function closeMark(ctx, type) {
  const index = ctx.tags.push({ type }) - 1;
  return "\u0000c" + index + "\u0001";
}

/** 只允许 http(s) / mailto / tel / 相对路径 / 锚点，挡掉 javascript: 这类协议 */
function safeUrl(url) {
  const value = String(url == null ? "" : url).trim();
  if (!value) return "";
  if (/^(https?:|mailto:|tel:)/i.test(value)) return value;
  /* 协议相对地址（//evil.com/a、\\evil.com/a）浏览器会解析成外站，
     和站内绝对路径 /foo 看起来只差一个字符，这里按不合法处理 */
  if (/^[\\/]{2}/.test(value)) return "";
  if (/^[a-z][a-z0-9+.-]*:/i.test(value)) return "";
  return value;
}

/* 颜色值白名单：十六进制 / rgb / rgba / hsl / hsla。
   语雀导出的是浏览器计算值，带透明度的元素会写成 rgba(...)，早期只认 rgb(...)，
   于是整对 <font> 匹配不上，开标签被转义成正文、闭标签却被吞掉。
   这里按「函数名 + 纯数字/百分数/逗号」严格校验，不含分号、url() 等可注入内容。 */
const HEX_COLOR_RE = /^#[0-9a-f]{3,8}$/i;
const COLOR_FUNCTION_RE =
  /^(?:rgb|rgba|hsl|hsla)\(\s*[-+.\d%]+(?:[\s,]+[-+.\d%]+){2,3}\s*\)$/i;

function isSupportedColorValue(value) {
  return HEX_COLOR_RE.test(value) || COLOR_FUNCTION_RE.test(value);
}

/** `<font style="...">` 只认 color / background-color 两个属性（按属性名，不枚举色值） */
function parseStyleDecls(style) {
  const result = {};
  String(style || "")
    .split(";")
    .forEach((decl) => {
      const colon = decl.indexOf(":");
      if (colon < 0) return;
      const prop = decl.slice(0, colon).trim().toLowerCase();
      const value = decl.slice(colon + 1).trim();
      if (
        (prop === "color" || prop === "background-color") &&
        isSupportedColorValue(value)
      ) {
        result[prop] = value;
      }
    });
  return result;
}

/** 从 `<font …>` 的属性串里取受支持的样式：style 里的 color / background-color，
 * 以及后台富文本偶尔会用的 color 属性形式（<font color="#ff0000">）。
 * 属性只允许 style / color，且必须是带引号的 `name="value"` 写法；
 * 出现 onclick 之类的未知属性时整对标签都不认（返回 null），保持原有安全口径。 */
function fontDeclsOf(attrs) {
  const text = String(attrs || "");
  const decls = {};
  const attrRe =
    /(?:^|\s)([a-z][a-z0-9:_-]*)\s*=\s*(?:"([^"]*)"|'([^']*)')/gi;
  let match = attrRe.exec(text);

  while (match) {
    const name = match[1].toLowerCase();
    const value = match[2] != null ? match[2] : match[3];
    if (name === "style") {
      Object.assign(decls, parseStyleDecls(value));
    } else if (name === "color") {
      const colorValue = value.trim();
      if (!decls.color && isSupportedColorValue(colorValue)) {
        decls.color = colorValue;
      }
    } else {
      return null;
    }
    match = attrRe.exec(text);
  }

  /* 属性串里还有无法识别的残留（无引号值、布尔属性等）也整体不认 */
  if (text.replace(attrRe, " ").trim()) return null;
  return decls;
}

function stripTags(html) {
  return String(html).replace(/<[^>]+>/g, "");
}

/** 语雀可能把 font 标签名导出成 **_font_**，先修复结构再解析强调。
 * 只还原这类标签中的转义尖括号和属性前的空格实体，正文及代码保持原样。 */
function repairFontTags(text) {
  return text.replace(
    /\\?<(\/?)\*\*_font_\*\*((?:"[^"]*"|'[^']*'|[^'">])*)>/gi,
    (match, closing, attrs) => {
      const rest = attrs.replace(/^(?:&#x20;|&#32;|&nbsp;)+/gi, " ");
      return (
        "<" +
        closing +
        "font" +
        /* 属性前必须是空白，否则 <font 会和属性名黏在一起 */
        (rest && !/^\s/.test(rest) ? " " : "") +
        rest +
        ">"
      );
    }
  );
}

/* =============================================================================
 * 行内解析
 * ========================================================================== */

/**
 * 把一段行内 Markdown 变成 React 子节点数组。
 * 思路和参照实现一致：先把不可再拆的东西（代码、图片、链接、白名单标签、硬换行）
 * 收成占位符，再对占位符外的文本做删除线 / 加粗 / 斜体正则，最后一次性还原成 React 树。
 * 这样 `**加粗和 **_**斜体**_** 套在一起**` 这种「加粗里套斜体」的语雀产物能正确解析。
 */
export function parseInline(input, ctx = createCtx()) {
  const text = String(input == null ? "" : input);
  const marked = applyEmphasis(extractAtoms(text, ctx), ctx);
  return buildInlineTree(marked, ctx);
}

function extractAtoms(text, ctx) {
  let t = text;

  /* 段落里的硬换行：段落解析阶段写入 \u0002 哨兵 */
  t = t.replace(/\u0002\s?/g, () => atom(ctx, h(ctx, "br", null)));

  /* 行内代码优先，避免里面的星号 / 下划线被当成强调 */
  t = t.replace(/`([^`]+)`/g, (match, code) => atom(ctx, h(ctx, "code", null, code)));

  /* 图片先于链接，避免 ![alt](url) 被链接规则吃掉。
     语雀 CDN（cdn.nlark.com）有防盗链：带 Referer 会 403，图片标签在、但根本加载不出来。
     这里统一用 no-referrer 请求，浏览器不带 Referer，CDN 才放行；正式接入时应把图片
     转存到自己的存储再改写地址（HANDLING.md 零之二），那时这个属性也无副作用。 */
  t = t.replace(/!\[([^\]]*)\]\(([^)\s]+)[^)]*\)/g, (match, alt, url) => {
    const src = safeUrl(url);
    if (!src) return match;
    return atom(
      ctx,
      h(ctx, "img", {
        src,
        alt,
        loading: "lazy",
        /* 语雀 CDN 防盗链：带 Referer 会 403。no-referrer 让浏览器不带 Referer，
           CDN 才放行；React 17 认 camelCase 的 referrerPolicy */
        referrerPolicy: "no-referrer",
      })
    );
  });

  /* 链接：文字继续走行内解析，所以链接里还能加粗、标红。
     目标地址允许一层成对括号（`javascript:alert(1)` 这种非法地址也要整段吃掉），
     否则降级成文字时会漏掉最后那个右括号。 */
  t = t.replace(
    /\[([^\]]+)\]\(((?:[^()\s]|\([^()]*\))+)[^)]*\)/g,
    (match, label, url) => {
      const href = safeUrl(url);
      if (!href) return label;
      const children = parseInline(label, ctx);
      return atom(
        ctx,
        h(
          ctx,
          "a",
          { href, target: "_blank", rel: "noopener noreferrer" },
          ...children
        )
      );
    }
  );

  t = repairFontTags(t);

  /* 转义标点作为文本原子保存，避免 \* 被加粗规则吃掉，或 \< 被当成标签。 */
  t = t.replace(
    /\\([!"#$%&'()*+,\-./:;<=>?@[\\\]^_`{|}~])/g,
    (match, char) => atom(ctx, char)
  );

  /* 白名单标签：<font style="color:…"> / <font style="background-color:…"> / <font color="…">
     开 / 闭标签在一次扫描里按栈配对处理：
       - 认得出的颜色 → 输出开 / 闭占位符，还原成 span / mark；
       - 认不出的标签 → 连开带闭一起丢掉，只保留内容。
     早期实现闭标签是无条件吞掉的，一旦开标签校验失败（例如 rgba 颜色）就会留下
     一个转义成正文、永远配不上闭标签的 <font>，页面上看就是「标签没有闭合」。 */
  const fontStack = [];
  t = t.replace(
    /* `(?=[\s/>])` 保证标签名就是 font，不会把 <font-size> 这类文本当成标签 */
    /<(\/?)font(?=[\s/>])((?:"[^"]*"|'[^']*'|[^'">])*)>/gi,
    (match, closing, attrs) => {
      if (closing) {
        return fontStack.pop() ? closeMark(ctx, "font") : "";
      }

      const decls = fontDeclsOf(attrs) || {};
      const recognized = Boolean(decls.color || decls["background-color"]);
      fontStack.push(recognized);
      if (!recognized) return "";

      if (decls["background-color"] && !decls.color) {
        return openMark(ctx, "font", "mark", {
          style: { background: decls["background-color"] },
        });
      }
      const style = {};
      if (decls.color) style.color = decls.color;
      if (decls["background-color"]) {
        style.background = decls["background-color"];
      }
      return openMark(ctx, "font", "span", { style });
    }
  );

  /* 白名单标签：<u> / <sup> / <sub> / <br> */
  t = t.replace(/<(u|sup|sub)>/gi, (match, tag) =>
    openMark(ctx, tag.toLowerCase(), tag.toLowerCase(), null)
  );
  t = t.replace(/<\/(u|sup|sub)\s*>/gi, (match, tag) =>
    closeMark(ctx, tag.toLowerCase())
  );
  t = t.replace(/<br\s*\/?>/gi, () => atom(ctx, h(ctx, "br", null)));

  return t;
}

function applyEmphasis(text, ctx) {
  let t = text;

  t = t.replace(
    /~~(.+?)~~/g,
    (match, inner) =>
      openMark(ctx, "del", "del", null) + inner + closeMark(ctx, "del")
  );

  /* 语雀把「加粗里套斜体」改写成 `** **` 相邻，这里按参照实现的做法：
     先加粗，再让斜体正则跨过加粗占位符配对 */
  t = t.replace(
    /\*\*(.+?)\*\*/g,
    (match, inner) =>
      openMark(ctx, "strong", "strong", null) + inner + closeMark(ctx, "strong")
  );

  t = t.replace(
    /(^|[^\w])_([^_]+)_(?=[^\w]|$)/g,
    (match, prefix, inner) =>
      prefix + openMark(ctx, "em", "em", null) + inner + closeMark(ctx, "em")
  );

  return t;
}

function buildInlineTree(marked, ctx) {
  const root = { children: [] };
  const stack = [{ type: "__root", children: root.children }];

  const pushText = (value) => {
    if (value) stack[stack.length - 1].children.push(value);
  };

  const closeTop = () => {
    const node = stack.pop();
    stack[stack.length - 1].children.push(
      h(ctx, node.tag, node.props, ...node.children)
    );
  };

  const closeTo = (type) => {
    for (let i = stack.length - 1; i > 0; i -= 1) {
      if (stack[i].type === type) {
        while (stack.length - 1 > i) closeTop();
        closeTop();
        return;
      }
    }
    /* 找不到对应开标签就忽略，和浏览器处理游离闭标签一致 */
  };

  let last = 0;
  marked.replace(MARK_RE, (match, kind, index, offset) => {
    pushText(marked.slice(last, offset));

    if (kind === "a") {
      stack[stack.length - 1].children.push(ctx.atoms[Number(index)]);
    } else if (kind === "o") {
      const tag = ctx.tags[Number(index)];
      stack.push({
        type: tag.type,
        tag: tag.tag,
        props: tag.props,
        children: [],
      });
    } else {
      closeTo(ctx.tags[Number(index)].type);
    }

    last = offset + match.length;
    return match;
  });
  pushText(marked.slice(last));

  while (stack.length > 1) closeTop();
  return root.children;
}

/* =============================================================================
 * 代码块：行号 + 语法高亮（色值见 index.less，来自语雀实测）
 * ========================================================================== */

function highlightLine(line, ctx) {
  const nodes = [];
  let buffer = "";
  let i = 0;

  const flushText = () => {
    if (buffer) {
      nodes.push(buffer);
      buffer = "";
    }
  };

  const pushToken = (className, value) => {
    flushText();
    nodes.push(h(ctx, "i", { className }, value));
  };

  while (i < line.length) {
    const char = line[i];

    /* 注释：// 或 # 到行尾 */
    if (char === "#" || (char === "/" && line[i + 1] === "/")) {
      pushToken("c", line.slice(i));
      i = line.length;
      continue;
    }

    /* 字符串：引号配对（和参照实现一致，不处理转义） */
    if (char === '"' || char === "'" || char === "`") {
      const end = line.indexOf(char, i + 1);
      if (end !== -1) {
        pushToken("s", line.slice(i, end + 1));
        i = end + 1;
        continue;
      }
      buffer += char;
      i += 1;
      continue;
    }

    /* 数字（含小数） */
    if (char >= "0" && char <= "9") {
      let end = i + 1;
      while (end < line.length && line[end] >= "0" && line[end] <= "9") {
        end += 1;
      }
      if (
        line[end] === "." &&
        line[end + 1] >= "0" &&
        line[end + 1] <= "9"
      ) {
        end += 1;
        while (end < line.length && line[end] >= "0" && line[end] <= "9") {
          end += 1;
        }
      }
      pushToken("n", line.slice(i, end));
      i = end;
      continue;
    }

    /* 标识符：关键字 / 函数名 / 普通文本 */
    if (/[A-Za-z_$]/.test(char)) {
      let end = i + 1;
      while (end < line.length && /[\w$]/.test(line[end])) end += 1;
      const word = line.slice(i, end);
      const prev = i > 0 ? line[i - 1] : "";
      const atWordStart = !/[\w$]/.test(prev);
      let next = end;
      while (next < line.length && (line[next] === " " || line[next] === "\t")) {
        next += 1;
      }
      if (atWordStart && KEYWORDS.has(word)) {
        pushToken("k", word);
      } else if (atWordStart && line[next] === "(") {
        pushToken("f", word);
      } else {
        buffer += word;
      }
      i = end;
      continue;
    }

    buffer += char;
    i += 1;
  }

  flushText();
  return nodes;
}

function buildCodeBlock(body, lang, ctx) {
  let text = body;
  /* 语雀用工具栏插的代码块，内容可能被额外包一层反引号 */
  if (/^`[\s\S]*`$/.test(text.trim())) text = text.trim().slice(1, -1);

  const lines = text.replace(/\n$/, "").split("\n");
  const rows = lines.map((line) => {
    const tokens = highlightLine(line, ctx);
    return h(
      ctx,
      "span",
      { className: "ln" },
      tokens.length ? tokens : "\u00a0"
    );
  });

  return h(
    ctx,
    "pre",
    lang ? { "data-lang": lang } : null,
    h(ctx, "code", null, ...rows)
  );
}

/* =============================================================================
 * 块级解析
 * ========================================================================== */

function isTableSeparator(line) {
  return /^\|[\s:|-]+\|?$/.test(line);
}

function isBlockStart(lines, index) {
  const raw = lines[index];
  const line = raw.trim();

  if (/^#{1,6}\s/.test(line)) return true;
  if (/^```/.test(line)) return true;
  /* 只有「下一行是分隔行」才算表格开始；普通以 | 开头的文字留在段落里，不能丢 */
  if (/^\|/.test(line)) {
    return (
      index + 1 < lines.length && isTableSeparator(lines[index + 1].trim())
    );
  }
  if (/^:::/.test(line)) return true;
  if (/^<details/i.test(line)) return true;
  if (/^\s*>/.test(raw)) return true;
  if (/^(-{3,}|\*{3,}|_{3,})$/.test(line)) return true;
  if (LIST_ITEM_RE.test(raw)) return true;
  if (STANDALONE_IMAGE_RE.test(line)) return true;
  return false;
}

/** 绝对层数 → 轮换的 list-style class（d1～d9，第 10 层回到 d1） */
function depthClass(depth) {
  return ((depth - 1) % 9) + 1;
}

function parseListItem(content, ctx) {
  const task = content.match(/^\[([ xX])\]\s+(.*)$/);
  if (task) {
    return {
      task: true,
      children: [
        h(ctx, "input", {
          type: "checkbox",
          disabled: true,
          defaultChecked: /[xX]/.test(task[1]),
        }),
        ...parseInline(task[2], ctx),
      ],
    };
  }
  return { task: false, children: parseInline(content, ctx) };
}

function parseList(lines, start, depth, ctx) {
  const first = lines[start].match(LIST_ITEM_RE);
  const baseIndent = first[1].length;
  const ordered = /\d/.test(first[2]);
  const items = [];
  let lastItem = null;
  let i = start;

  while (i < lines.length) {
    const raw = lines[i];
    const line = raw.trim();

    /* 空行不结束列表：下一个非空行还是同层 / 更深层的列表项时才继续 */
    if (!line) {
      let nextLine = i + 1;
      while (nextLine < lines.length && !lines[nextLine].trim()) nextLine += 1;
      if (nextLine >= lines.length) break;
      const nextItem = lines[nextLine].match(LIST_ITEM_RE);
      if (nextItem && nextItem[1].length >= baseIndent) {
        i = nextLine;
        continue;
      }
      break;
    }

    const item = raw.match(LIST_ITEM_RE);
    if (!item) break;

    const indent = item[1].length;
    if (indent < baseIndent) break;

    if (indent > baseIndent) {
      if (!lastItem) break;
      const nested = parseList(lines, i, depth + 1, ctx);
      /* 嵌套列表挂进上一项的 li 里，保持合法 DOM 结构 */
      lastItem.children.push(nested.node);
      i = nested.next;
      continue;
    }

    /* 同层但有序 / 无序切换：结束当前列表，外层会另起一个 */
    if (/\d/.test(item[2]) !== ordered) break;

    lastItem = parseListItem(item[3], ctx);
    items.push(lastItem);
    i += 1;
  }

  const Tag = ordered ? "ol" : "ul";
  const children = items.map((item) =>
    h(
      ctx,
      "li",
      item.task ? { className: "task" } : null,
      ...item.children
    )
  );

  return {
    node: h(ctx, Tag, { className: "d" + depthClass(depth) }, ...children),
    next: i,
  };
}

function parseTable(lines, start, ctx) {
  const cellsOf = (row) =>
    row
      .trim()
      .replace(/^\||\|$/g, "")
      .split("|")
      .map((cell) => cell.trim());

  const head = cellsOf(lines[start]);
  const align = cellsOf(lines[start + 1]).map((cell) =>
    /^:.*:$/.test(cell) ? "center" : /:$/.test(cell) ? "right" : "left"
  );

  let i = start + 2;
  const rows = [];
  while (i < lines.length && /^\|/.test(lines[i].trim())) {
    rows.push(cellsOf(lines[i]));
    i += 1;
  }

  const headCells = head.map((cell, index) =>
    h(
      ctx,
      "th",
      { style: { textAlign: align[index] || "left" } },
      ...parseInline(cell, ctx)
    )
  );

  const bodyRows = rows.map((row) =>
    h(
      ctx,
      "tr",
      null,
      ...row.map((cell, index) =>
        h(
          ctx,
          "td",
          { style: { textAlign: align[index] || "left" } },
          ...parseInline(cell, ctx)
        )
      )
    )
  );

  return {
    node: h(
      ctx,
      "div",
      { className: "tablewrap" },
      h(
        ctx,
        "table",
        null,
        h(ctx, "thead", null, h(ctx, "tr", null, ...headCells)),
        h(ctx, "tbody", null, ...bodyRows)
      )
    ),
    next: i,
  };
}

/** 语雀折叠块：丢掉内部 class / id，只用文字重建干净的 details（默认收起） */
function buildDetails(html, ctx) {
  const summaryMatch =
    /<summary[^>]*>([\s\S]*?)<\/summary>/i.exec(html);
  const summaryText = summaryMatch
    ? stripTags(summaryMatch[1]).trim()
    : "详情";
  const body = html
    .replace(/^[\s\S]*?<\/summary>/i, "")
    .replace(/<\/details>[\s\S]*$/i, "");
  const paragraphs = body
    .split(/<\/p>/i)
    .map((part) => stripTags(part).trim())
    .filter(Boolean);

  return h(
    ctx,
    "details",
    null,
    h(ctx, "summary", null, ...parseInline(summaryText, ctx)),
    ...paragraphs.map((paragraph) =>
      h(ctx, "p", null, ...parseInline(paragraph, ctx))
    )
  );
}

function parseBlocks(markdown, ctx) {
  const lines = String(markdown).split("\n");
  const out = [];
  let i = 0;

  while (i < lines.length) {
    const raw = lines[i];
    const line = raw.trim();

    if (!line) {
      i += 1;
      continue;
    }

    /* :::info 高亮块（语雀叫「高亮块」，导出统一是 info） */
    if (/^:::/.test(line)) {
      const type = (line.slice(3).trim() || "info").toLowerCase();
      const buffer = [];
      i += 1;
      while (i < lines.length && !/^:::\s*$/.test(lines[i].trim())) {
        buffer.push(lines[i]);
        i += 1;
      }
      i += 1;
      out.push(
        h(
          ctx,
          "div",
          { className: "callout", "data-type": type },
          ...parseBlocks(buffer.join("\n"), ctx)
        )
      );
      continue;
    }

    /* 折叠 / 详情块：一整行裸 HTML，重建成干净的 details */
    if (/^<details/i.test(line)) {
      const buffer = [];
      while (i < lines.length) {
        buffer.push(lines[i]);
        const done = /<\/details>/i.test(lines[i]);
        i += 1;
        if (done) break;
      }
      out.push(buildDetails(buffer.join("\n"), ctx));
      continue;
    }

    /* 代码块 */
    if (/^```/.test(line)) {
      const lang = line.slice(3).trim();
      const buffer = [];
      i += 1;
      while (i < lines.length && !/^```/.test(lines[i].trim())) {
        buffer.push(lines[i]);
        i += 1;
      }
      i += 1;
      out.push(buildCodeBlock(buffer.join("\n"), lang, ctx));
      continue;
    }

    /* 表格：当前行 + 下一行是表头 / 分隔行 */
    if (/^\|/.test(line) && i + 1 < lines.length && isTableSeparator(lines[i + 1].trim())) {
      const table = parseTable(lines, i, ctx);
      out.push(table.node);
      i = table.next;
      continue;
    }

    /* 分割线 */
    if (/^(-{3,}|\*{3,}|_{3,})$/.test(line)) {
      out.push(h(ctx, "hr", null));
      i += 1;
      continue;
    }

    /* 标题：1～6 级都输出（5/6 级用四级版式，见 index.less） */
    const heading = line.match(/^(#{1,6})\s+(.*)$/);
    if (heading) {
      const level = heading[1].length;
      out.push(
        h(ctx, "h" + level, null, ...parseInline(heading[2], ctx))
      );
      i += 1;
      continue;
    }

    /* 引用：连续的 > 行合并后递归解析，支持引用里再套列表 / 代码块 */
    if (/^>\s?/.test(line)) {
      const buffer = [];
      while (i < lines.length && /^\s*>/.test(lines[i])) {
        buffer.push(lines[i].replace(/^\s*>\s?/, ""));
        i += 1;
      }
      out.push(
        h(ctx, "blockquote", null, ...parseBlocks(buffer.join("\n"), ctx))
      );
      continue;
    }

    /* 列表 */
    if (LIST_ITEM_RE.test(raw)) {
      const list = parseList(lines, i, 1, ctx);
      out.push(list.node);
      i = list.next;
      continue;
    }

    /* 独占一行的图片 */
    if (STANDALONE_IMAGE_RE.test(line)) {
      out.push(h(ctx, "figure", null, ...parseInline(line, ctx)));
      i += 1;
      continue;
    }

    /* 段落：吃到空行或下一个块开始；行尾两个空格 = 硬换行 */
    const buffer = [];
    while (i < lines.length) {
      const current = lines[i];
      const currentLine = current.trim();
      if (!currentLine) break;
      if (isBlockStart(lines, i)) break;
      buffer.push(
        /[ \t]{2,}$/.test(current)
          ? currentLine + HARD_BREAK_MARKER
          : currentLine
      );
      i += 1;
    }
    if (!buffer.length) {
      i += 1;
      continue;
    }
    out.push(h(ctx, "p", null, ...parseInline(buffer.join(" "), ctx)));
  }

  return out;
}

/**
 * Markdown 字符串 → React 子节点数组。
 * 纯函数，不依赖 DOM / 样式；组件层只负责套根容器和引入样式。
 */
export default function parseYuqueMarkdown(markdown) {
  const ctx = createCtx();
  const source = String(markdown == null ? "" : markdown)
    /* 兼容带 UTF-8 BOM 的字符串（本地读文件 / 后端拼接都可能带） */
    .replace(/^\uFEFF/, "")
    /* 去掉正文里不可能用到的 C0 控制字符，避免和内部占位符冲突 */
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, "")
    /* 语雀导出器会在每张图片前插一行 OCR 注释，整段剥掉 */
    .replace(HTML_COMMENT_RE, "");

  return parseBlocks(source, ctx);
}
